import { useAppSelector } from '@/store/hooks/redux';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { FC, useEffect, useRef, useState, type JSX } from 'react';
import { IExtendedPreviewPropsModel } from '@/editor/shared/components/forms/creatives/models/basic-preview-props.model';
import { bidtheatreClientStyles } from './BidtheatreClientStyle';
import CreativeTypesEnum from '@/editor/shared/constants/creatives-types.enum';
import EmptyImageCard from '../EmptyImageCard';
import { useFormikContext } from 'formik';


const MaklerBidtheatrePreview: FC<IExtendedPreviewPropsModel> = ({
    caption,
    profileTitle,
    municipality,
    employeeName,
    employeeAvatar,
    companyExternalId,
    selectedType,
    profileImage,
}): JSX.Element => {
    const bidtheatreClientStyle = companyExternalId ? bidtheatreClientStyles[companyExternalId] || {} : {};
    const singleMedia = useAppSelector(
        (state) => state.creativeStepSliceReducer?.creativeFormsState?.bidtheatre?.single?.media
    );
    const dynamicMedia = useAppSelector(
        (state) => state.creativeStepSliceReducer?.creativeFormsState?.bidtheatre?.dynamic?.creativeMedias
    );
    const bidtheatreShareLink = useAppSelector((state) => state.propertyInfoSliceReducer.bidtheatreShareLink);

    const [avatarUrl, setAvatarUrl] = useState<string | undefined>(undefined);
    const [index, setIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(true);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const totalSlides = dynamicMedia.length;
    const formik = useFormikContext();

    useEffect(() => {
        if (selectedType === CreativeTypesEnum.DYNAMIC) {
            startAutoSlide();
            return stopAutoSlide;
        }
    }, [selectedType]);
    const startAutoSlide = () => {
        intervalRef.current = setInterval(() => {
            handleNext();
        }, 4000);
    };

    const stopAutoSlide = () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
    };

    const handleNext = () => {
        if (index === totalSlides) return;
        setIsTransitioning(true);
        setIndex((prev) => prev + 1);
    };

    useEffect(() => {
        if (index === totalSlides) {
            const timeout = setTimeout(() => {
                setIsTransitioning(false);
                setIndex(0);
            }, 600);
            return () => clearTimeout(timeout);
        }
    }, [index, totalSlides]);

    useEffect(() => {
        if (employeeAvatar instanceof File) {
            const url = URL.createObjectURL(employeeAvatar);
            setAvatarUrl(url);
            return () => URL.revokeObjectURL(url);
        } else if (profileImage) {
            setAvatarUrl(profileImage);
            formik?.setFieldValue('employeeAvatar', profileImage)!;
        } else {
            setAvatarUrl(employeeAvatar);
        }
    }, [employeeAvatar]);

    return (
        <Grid
            container
            sx={{
                border: '1px solid #231464',
                maxWidth: { xs: '100vw', sm: '700px' },
                height: 'auto',
                overflow: 'hidden',
            }}>
            <Grid item xs={12} sx={{ minHeight: '325px', pb: 1, backgroundColor: '#404041' }}>
                {selectedType === CreativeTypesEnum.SINGLE || selectedType === CreativeTypesEnum.CAROUSEL ? (
                    singleMedia?.preSignedUrl ? (
                        <Grid
                            component="a"
                            target="_blank"
                            href={bidtheatreShareLink}
                            sx={{
                                textDecoration: 'none',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                position: 'relative',
                                borderLeft: { xs: '20px solid transparant', sm: '35px solid transparant' },
                                borderRight: { xs: '40px solid transparant', sm: '80px solid transparant' },
                            }}>
                            <Box sx={{ position: 'relative', width: '100%', height: '390px', overflow: 'hidden' }}>
                                <img
                                    src={singleMedia?.preSignedUrl}
                                    alt="property"
                                    style={{
                                        width: '100%',
                                        height: '382px',
                                        objectFit: 'cover',
                                    }}
                                />
                            </Box>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: { xs: '-55px', sm: '-80px', md: '-100px' },
                                    left: '85%',
                                    width: '38%',
                                    transform: 'translateX(-50%)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                }}>
                                <img
                                    style={{
                                        height: 'auto',
                                        maxWidth: '-webkit-fill-available',
                                        objectFit: 'cover',
                                        width: '80%'
                                    }}
                                    src={avatarUrl || undefined}
                                    alt="avatar"
                                />
                                <Box sx={{ color: '#FAE8DC', backgroundColor: '#404041', position: 'absolute', bottom: { md: '22px', xs: '8px' }, left: { md: '60px', xs: '25px', sm: '80px' } }}>
                                    <Typography
                                        sx={{
                                            fontWeight: '600',
                                            fontSize: { xs: '4px', sm: '10px' },
                                            fontFamily: bidtheatreClientStyle.fontFamily || '',
                                        }}>
                                        {employeeName}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontWeight: '600',
                                            fontSize: { xs: '4px', sm: '10px' },
                                            fontFamily: bidtheatreClientStyle.fontFamily || '',
                                        }}>
                                        {profileTitle}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    ) : (
                        <EmptyImageCard></EmptyImageCard>
                    )
                ) : dynamicMedia?.[0]?.preSignedUrl ? (
                    <Grid
                        item
                        component="a"
                        target="_blank"
                        href={bidtheatreShareLink}
                        sx={{
                            textDecoration: 'none',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            position: 'relative',
                            borderLeft: { xs: '20px solid transparant', sm: '35px solid transparant' },
                            borderRight: { xs: '40px solid transparant', sm: '80px solid transparant' },
                        }}
                        xs={12}
                        className="eie-preview-box">
                        <Box
                            sx={{
                                width: '750px',
                                height: '416px',
                                overflow: 'hidden',
                                margin: 'auto',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    width: `${750 * (totalSlides + 1)}px`,
                                    transform: `translateX(-${index * 750}px)`,
                                    transition: isTransitioning ? 'transform 0.6s ease-in-out' : 'none',
                                }}
                                onTransitionEnd={() => setIsTransitioning(true)}
                            >
                                {dynamicMedia.map((src, i) => (
                                    <img
                                        key={i}
                                        src={src.preSignedUrl}
                                        alt={`slide-${i}`}
                                        style={{ width: '750px', height: '382px', objectFit: 'cover' }}
                                    />
                                ))}
                                <img
                                    src={dynamicMedia[0].preSignedUrl}
                                    alt="cloned-slide"
                                    style={{ width: '750px', height: '382px', objectFit: 'cover' }}
                                />
                            </Box>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: { xs: '-60px', sm: '-10px', md: '-100px' },
                                    left: '89%',
                                    width: '26%',
                                    transform: 'translateX(-50%)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                }}>
                                <img
                                    style={{
                                        height: 'auto',
                                        maxWidth: '-webkit-fill-available',
                                        objectFit: 'cover',
                                    }}
                                    src={avatarUrl || undefined}
                                    alt="avatar"
                                />
                                <Box sx={{ color: '#FAE8DC', backgroundColor: '#404041', position: 'absolute', bottom: '22px', left: '60px' }}>
                                    <Typography
                                        sx={{
                                            fontWeight: '600',
                                            fontSize: { xs: '4px', sm: '10px' },
                                            fontFamily: bidtheatreClientStyle.fontFamily || '',
                                        }}>
                                        {employeeName}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontWeight: '600',
                                            fontSize: { xs: '4px', sm: '10px' },
                                            fontFamily: bidtheatreClientStyle.fontFamily || '',
                                        }}>
                                        {profileTitle}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                ) : (<EmptyImageCard />
                )}
                <Box>
                    <Box sx={{ color: '#FAE8DC', marginLeft: '6%' }}>
                        <Typography
                            sx={{
                                fontWeight: '600',
                                fontSize: { xs: '12px', sm: '20px', md: '15px' },
                                fontFamily: bidtheatreClientStyle.fontFamily || '',
                            }}>
                            {caption}
                        </Typography>
                        <Typography
                            sx={{
                                fontWeight: '600',
                                fontSize: { xs: '12px', sm: '15px', md: '15px' },
                                fontFamily: bidtheatreClientStyle.fontFamily || '',
                            }}>
                            {municipality}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            float: 'left',
                            position: 'relative',
                            color: '#FAE6DC',
                            marginLeft: '6%',
                        }}>
                        <img
                            style={{
                                width: '66%',
                            }}
                            src={bidtheatreClientStyle.logo || ''}
                            alt="logo"
                        />
                    </Box>
                </Box>
            </Grid>
        </Grid >
    );
};

export default MaklerBidtheatrePreview;